import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface StandingEntry {
  competitorName: string;
  memberSub: string | undefined;
  /** A8: present when this entry is keyed by memberId (no login), not memberSub. */
  memberId?: string | undefined;
  totalPoints: number;
  division: 'SENIOR' | 'JUNIOR';
  recommendedChampion: boolean;
  tiebreakerValues?: {
    earliestCatchTime: string | null;
    catchCount: number;
    biggestFishCm: number;
    cumulativeSeasonPoints: number;
  };
}

/** A8: a placed catch with neither memberSub nor memberId, admin only (no champion points possible). */
interface NoIdentityEntry {
  id: string;
  competitorName: string | undefined;
  species: string;
  rank: number;
}

interface ConfirmedChampion {
  name: string;
  points: number;
  memberSub: string;
}

interface ChampionPointsPanelProps {
  eventId: string;
  adjudicated: boolean;
  published: boolean;
  seniorStandings: StandingEntry[];
  juniorStandings: StandingEntry[];
  confirmedSeniorChampion: ConfirmedChampion | null;
  confirmedJuniorChampion: ConfirmedChampion | null;
  onChampionConfirmed: () => void;
  /** A8: placed catches with no resolvable member identity, admin only. */
  championNoIdentity?: NoIdentityEntry[];
}

const ChampionPointsPanel: React.FC<ChampionPointsPanelProps> = ({
  eventId, adjudicated, published, seniorStandings, juniorStandings,
  confirmedSeniorChampion, confirmedJuniorChampion, onChampionConfirmed,
  championNoIdentity = [],
}) => {
  const [seniorSelection, setSeniorSelection] = useState<string>(confirmedSeniorChampion?.memberSub || '');
  const [juniorSelection, setJuniorSelection] = useState<string>(confirmedJuniorChampion?.memberSub || '');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const confirmChampion = async (division: 'SENIOR' | 'JUNIOR') => {
    const standings = division === 'SENIOR' ? seniorStandings : juniorStandings;
    const selection = division === 'SENIOR' ? seniorSelection : juniorSelection;
    const entry = standings.find(s => s.memberSub === selection);
    if (!entry?.memberSub) return;

    setSaving(true);
    try {
      const result = await (client.mutations as any).confirmChampion({
        eventId,
        division,
        championName: entry.competitorName,
        championMemberSub: entry.memberSub,
        championPoints: entry.totalPoints,
      });
      const data = parseLambdaResponse(result?.data);
      if (!data?.success) throw new Error(data?.error || 'Failed');
      setToast(`${division} champion confirmed: ${entry.competitorName}`);
      setTimeout(() => setToast(null), 3000);
      onChampionConfirmed();
    } catch (err: any) {
      setToast(`Error: ${err.message}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  // #134 D6: a champion can be confirmed once the competition is
  // adjudicated, and editing stays allowed after publish (unchanged).
  const canEdit = adjudicated || published;

  const renderDivision = (
    label: string, standings: StandingEntry[],
    confirmed: ConfirmedChampion | null,
    selection: string, setSelection: (v: string) => void,
    division: 'SENIOR' | 'JUNIOR',
  ) => (
    <div className="mb-6">
      <h4 className="text-md font-semibold text-gray-800 mb-2">{label}</h4>
      {confirmed && (
        <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <span className="text-sm font-medium text-yellow-800">🏆 Confirmed: {confirmed.name} ({confirmed.points} pts)</span>
        </div>
      )}
      {standings.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No points scored</p>
      ) : (
        <>
          <table className="w-full text-sm mb-3">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-1 pr-2">#</th>
                <th className="py-1">Name</th>
                <th className="py-1 text-right">Points</th>
                <th className="py-1 text-center">Rec.</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={s.memberSub || i} className={`border-b ${s.recommendedChampion ? 'bg-blue-50' : ''}`}>
                  <td className="py-1 pr-2 text-gray-600">{i + 1}</td>
                  <td className="py-1">
                    {s.competitorName}
                    {!s.memberSub && s.memberId && (
                      <span className="ml-2 text-xs text-gray-500">No login (member record)</span>
                    )}
                  </td>
                  <td className="py-1 text-right font-medium">{s.totalPoints}</td>
                  <td className="py-1 text-center">{s.recommendedChampion ? '⭐' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {canEdit && (
            <div className="flex items-center gap-2">
              <select
                value={selection}
                onChange={e => setSelection(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1 flex-1"
              >
                <option value="">Select champion...</option>
                {standings.map((s, i) => (
                  <option key={s.memberSub || s.memberId || i} value={s.memberSub || ''}>
                    {s.competitorName}{!s.memberSub && s.memberId ? ' (No login (member record))' : ''} ({s.totalPoints} pts){s.recommendedChampion ? ' ⭐' : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={() => confirmChampion(division)}
                disabled={saving || !selection}
                className="px-3 py-1 text-sm font-medium text-white bg-fishing-blue hover:bg-blue-700 rounded disabled:bg-gray-300"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">🏆 Champion Points</h3>
      {toast && (
        <div className="mb-3 p-2 text-sm rounded bg-blue-50 text-blue-800">{toast}</div>
      )}
      {renderDivision('Senior Division', seniorStandings, confirmedSeniorChampion, seniorSelection, setSeniorSelection, 'SENIOR')}
      {renderDivision('Junior Division', juniorStandings, confirmedJuniorChampion, juniorSelection, setJuniorSelection, 'JUNIOR')}

      {championNoIdentity.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">These catches earn no champion points: no member identity.</p>
          <ul className="mt-2 space-y-1 text-sm text-amber-800">
            {championNoIdentity.map((c) => (
              <li key={c.id}>{c.competitorName || 'Unnamed angler'}: {c.species}, placed {c.rank}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChampionPointsPanel;
